/** @jsxImportSource @emotion/react */
import { css } from "@emotion/react";
import styled from "@emotion/styled";
import React, {
  DetailedHTMLProps,
  InputHTMLAttributes,
  useEffect,
  useRef,
  useState,
} from "react";
import { IoMdClose, IoMdSearch } from "react-icons/io";
import { toast } from "react-toastify";
import ReactTooltip from "react-tooltip";
import { useRecoilState, useRecoilValue } from "recoil";
import { CategoriesAPI, protectedCategories } from "../../../api/Categories";
import { CategoriesAtom } from "../../../atoms/CategoriesAtom";
import { UserAtom } from "../../../atoms/UserAtom";
import { ICategoryType } from "../../../generated/Api";
import useClickOutside from "../../../hook/useClickOutside";
import { Ellipsis } from "../../../styles/general";
import { getScrollParent } from "../../../utils/DOMHelper";
import { default as Icon, default as IconBerserkerMail } from "../../Icon";
import StyledButton from "../../StyledButton";
import { AutoCompleteSelectItem } from "../../TagSelect/AutoCompleteItem";

const Container = styled.div`
  width: 100%;
  height: 40px;
  display: flex;
  position: relative;
`;

const InputContainer = styled.div`
  width: 100%;

  .__react_component_tooltip {
    padding: 8px !important;
    background: #c41700 !important;
    &::after {
      left: 5% !important;
    }
  }
`;

const SearchIcon = styled.div`
  position: absolute;
  left: 8px;
  top: 10px;
  svg {
    color: #cccccc;
  }
`;

interface InputProps {
  hasError: boolean;
}

const Input = styled.input<InputProps>`
  width: 100%;
  height: 40px;
  border: 1px solid #dddddd;
  border-color: #dddddd;

  border-radius: 5px;
  outline: none;
  font-size: 14px;
  padding: 5px;
  color: #383838;
  text-indent: 25px;
  background-color: #ffffff;

  transition: border-color 0.3s, box-shadow 0.3s, border 0.3s;

  &:focus {
    box-shadow: 0px 0px 6px #fb972e4d;
    border-color: #fb972e;
  }

  &::placeholder {
    font-size: 14px;
    letter-spacing: 0.16px;
    color: #979797;
  }

  ${(props) =>
    props.hasError &&
    css`
      border-color: #c41700;
      box-shadow: 0px 0px 6px #c417004d;
    `}
`;

const AutoCompleteDiv = styled.div`
  display: flex;
  flex-direction: row;
  align-items: center;
  width: 100%;
  border-radius: 5px;
  background-color: #ffffff;
`;

interface DropViewProps {
  position: { top: number; left: number };
  width: number;
}

const StyledAutoCompleteList = styled.div`
  position: fixed;
  background-color: #fff;
  z-index: 15;
  max-height: 260px;
  flex: 1;
  overflow: auto;
  border-radius: 5px;
  box-shadow: 0px 3px 6px #0000001a;
  border: 1px solid #d4d4d4;
  border-bottom: none;
  border-top: none;
  right: 0;
  top: ${(props: DropViewProps) => props.position.top}px;
  left: ${(props: DropViewProps) => props.position.left}px;
  width: ${(props: DropViewProps) => props.width}px;
  cursor: default;
`;

const TooltipText = styled.span`
  font-size: 14px;
  font-weight: 600;
  letter-spacing: 0px;
  color: #ffffff;
`;

const NoResultsContainer = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  background-color: #fff;
  color: #000;
  padding: 20px 15px 30px 15px;
  text-align: center;
`;
const IconContainer = styled.div``;

const TitleNoResults = styled.div`
  margin-top: 10px;
  font-weight: bold;
  margin-bottom: 12px;
`;
const Content = styled.div`
  color: #666666;
  margin-bottom: 5px;
`;

const CreateTagButton = styled(StyledButton)`
  margin-top: 12px;
`;

const CreateCategory = styled.div`
  display: flex;
  flex-direction: row;
  align-items: center;
  padding: 10px;
  cursor: pointer;
  border-top: 1px solid #ebebeb;

  &:hover {
    background-color: #fb972e;
    color: #fff;

    svg {
      path {
        fill: #fff;
      }
    }
  }
`;

const CreateCategoryTitle = styled.span`
  font-size: 16px;
  margin-left: 5px;
  font-weight: normal;
  display: flex;
  align-items: center;
  width: 100%;
`;

const StyledClearIcon = styled.div<{ isCreatingTag: boolean }>`
  position: absolute;
  cursor: pointer;
  right: ${(props) => (props.isCreatingTag ? "80px" : "14px")};
  top: 13px;
  svg {
    color: #868482;
    opacity: 1;
    transition: opacity 0.3s;
    :hover {
      opacity: 0.8;
    }
  }
`;

interface ICategoryOption {
  type: ICategoryType;
  name: string;
  id: string;
}

type IStyledAutocompleteSelectProps = DetailedHTMLProps<
  InputHTMLAttributes<HTMLInputElement>,
  HTMLInputElement
> & {
  placeholder?: string;
  suggestions: ICategoryOption[] | undefined;
  selectedValue: string;
  onEnter: (options: ICategoryOption) => void;
  onValueChange?: (value: string) => void;
  onValueSelect: (options: ICategoryOption) => void;
  onCancel: () => void;
  optionIsSelected: boolean;
};

interface IItem {
  groupName?: string;
  name: string;
  icon?: string;
  suffix?: string;
  index: number;
  isActive: boolean;
  type: ICategoryType;
}

export default function AutoCompleteCreateCategory(
  props: IStyledAutocompleteSelectProps
): JSX.Element {
  const {
    suggestions,
    selectedValue,
    onValueSelect,
    onValueChange,
    placeholder,
    optionIsSelected,
  } = props;
  const [currentFocus, setCurrentFocus] = useState(0);
  const [filteredSuggestions, setFilteredSuggestions] = useState<IItem[]>();
  const user = useRecoilValue(UserAtom);
  const [hasError, setHasError] = useState(false);
  /**
   * Select functionality
   */
  const [openDropView, setOpenDropView] = useState<boolean>(false);

  /**
   * Refs to keep track
   */
  const wrapperRef = useClickOutside(() => setOpenDropView(false));
  const selectRef = useRef<HTMLDivElement | null>(null);
  const containerRef = useRef<HTMLDivElement | null>(null);
  const suggestionRefMap = useRef<{
    [suggestionValue: string]: HTMLDivElement | null;
  }>({});
  const [categories, setCategories] = useRecoilState(CategoriesAtom);

  const [dropviewPosition, setDropViewPosition] = useState<{
    top: number;
    left: number;
  }>({
    top: 0,
    left: 0,
  });
  const [dropViewWidth, setDropViewWidth] = useState<number>(0);
  /**
   * Update DropView position to match current Input location on screen.
   *
   * This function moves the DropView below the Input position.
   */
  function updateDropViewPosition() {
    if (!wrapperRef.current) return;

    const elementBoundingRect = (
      wrapperRef.current as HTMLDivElement
    ).getBoundingClientRect();

    setDropViewPosition({
      top: elementBoundingRect.top + elementBoundingRect.height,
      left: elementBoundingRect.left,
    });

    setDropViewWidth(elementBoundingRect.width);
  }

  /**
   * useEffect that closes the DropView when a scroll is detected, much like
   * the native <select />
   */
  useEffect(() => {
    if (!openDropView) {
      setCurrentFocus(0);
      return () => {};
    }

    function closeDropView() {
      setOpenDropView(false);
    }

    const scrollParent = getScrollParent(selectRef.current, selectRef.current);

    if (!scrollParent) return () => {};

    scrollParent.addEventListener("scroll", closeDropView);

    return () => {
      scrollParent.removeEventListener("scroll", closeDropView);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [wrapperRef, openDropView]);

  useEffect(() => {
    if (suggestions) {
      const suggestionsArray = suggestions
        .map((suggestion, index) => ({
          ...suggestion,
          index,
          isActive: false,
        }))
        .filter((suggestion) => {
          return suggestion.name
            .toLocaleLowerCase()
            .includes(selectedValue.toLocaleLowerCase());
        });
      setFilteredSuggestions(suggestionsArray);
    }
  }, [suggestions]);

  /**
   * On window resize, calculate position
   */
  useEffect(() => {
    window.addEventListener("resize", updateDropViewPosition);
    return () => window.removeEventListener("resize", updateDropViewPosition);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [wrapperRef]);

  useEffect(() => {
    setFilteredSuggestions((prevSuggestionList) => {
      if (prevSuggestionList) {
        return prevSuggestionList.map((suggestionItem, index) => {
          if (index === currentFocus) {
            return {
              ...suggestionItem,
              isActive: true,
            };
          }
          return {
            ...suggestionItem,
            isActive: false,
          };
        });
      }
      return undefined;
    });
  }, [currentFocus]);

  function typedValidValue(): ICategoryOption | undefined {
    if (suggestions && selectedValue) {
      const tag = suggestions.find((suggestion) => {
        return suggestion.name === selectedValue;
      });
      return tag;
    }
    return undefined;
  }

  function getContainerWidth() {
    if (!containerRef.current) return 0;

    const elementBoundingRect = (
      containerRef.current as HTMLDivElement
    ).getBoundingClientRect();

    return (elementBoundingRect.width - 330) / 2;
  }

  function handleClick() {
    setHasError(false);
    updateDropViewPosition();
    setOpenDropView(true);

    setTimeout(() => {
      document.getElementById("input-category").focus();
    }, 100);
  }

  function handleLoseFocus() {
    const category = typedValidValue();
    // The value he typed is already a value in the suggestion list
    if (category) {
      setOpenDropView(false);
      onValueSelect({
        id: `${category.id}`,
        name: category.name,
        type: category.type,
      });
    } else {
      setHasError(true);
    }
  }

  function handleItemClick(item: IItem) {
    setHasError(false);
    if (suggestions) {
      const category = suggestions.find(
        (suggestion) => suggestion.name === item.name
      ) as ICategoryOption;

      setOpenDropView(false);

      onValueSelect({
        id: `${category.id}`,
        name: category.name,
        type: category.type,
      });
    }
  }

  function handleInputKeyDown(e: React.KeyboardEvent) {
    if (suggestions && filteredSuggestions) {
      const lastItem = filteredSuggestions.length - 1;
      if (e.key === "ArrowDown") {
        e.preventDefault();

        let currentIndex = 0;

        if (currentFocus === lastItem) {
          setCurrentFocus(0);
        } else {
          setCurrentFocus(currentFocus + 1);
          currentIndex = currentFocus + 1;
        }

        let currentSuggestionActive = filteredSuggestions[currentIndex];

        suggestionRefMap.current[currentSuggestionActive.name]?.scrollIntoView({
          behavior: "smooth",
          block: "center",
        });
      } else if (e.key === "ArrowUp") {
        e.preventDefault();

        let currentIndex = lastItem;
        if (currentFocus === 0) {
          setCurrentFocus(lastItem);
        } else {
          setCurrentFocus(currentFocus - 1);
          currentIndex = currentFocus - 1;
        }

        let currentSuggestionActive = filteredSuggestions[currentIndex];

        suggestionRefMap.current[currentSuggestionActive.name]?.scrollIntoView({
          behavior: "smooth",
          block: "center",
        });
      } else if (e.key === "Enter") {
        e.preventDefault();
        if (currentFocus > -1) {
          if (filteredSuggestions[currentFocus]) {
            const category = suggestions[currentFocus];
            setOpenDropView(false);

            onValueSelect({
              id: `${category.id}`,
              name: category.name,
              type: category.type,
            });
          }
        } else {
          const category = typedValidValue();
          if (category) {
            setOpenDropView(false);

            onValueSelect({
              id: `${category.id}`,
              name: category.name,
              type: category.type,
            });
          }
        }
      }
    }
  }

  function clearInput() {
    handleInputChange("");
  }

  async function handleCategoryValueSelect() {
    if (!user) return;

    const foundSameCat = categories.find(
      (c) =>
        c.name.toLowerCase().replace(/\s\s+/g, " ").trim() ===
        selectedValue.toLowerCase().replace(/\s\s+/g, " ").trim()
    );

    const isProtected = protectedCategories.find(
      (c) =>
        c.toLowerCase().replace(/\s\s+/g, " ").trim() ===
        selectedValue.toLowerCase().replace(/\s\s+/g, " ").trim()
    );

    if (!selectedValue?.replace(/\s\s+/g, " ").trim()) {
      toast("Can't create a category with empty name", { type: "info" });
    } else if (foundSameCat) {
      toast("Can't create category with same name", { type: "info" });
    } else if (isProtected) {
      toast("Can't create category with system category name", {
        type: "info",
      });
    } else {
      const category = await CategoriesAPI.create({
        name: selectedValue.replace(/\s\s+/g, " ").trim(),
        type: ICategoryType.User,
      });
      if (category) {
        setOpenDropView(false);

        setCategories([
          ...categories,
          {
            _id: category._id,
            name: selectedValue,
            type: ICategoryType.User,
          },
        ]);

        onValueSelect({
          id: category._id,
          name: selectedValue,
          type: ICategoryType.User,
        });
      }
    }
  }

  function handleInputChange(value: string) {
    setHasError(false);
    setCurrentFocus(-1);

    if (onValueChange) onValueChange(value);
    if (suggestions) {
      const suggestionsArray = suggestions
        .map((suggestion, index) => ({
          ...suggestion,
          index,
          isActive: false,
        }))
        .filter((suggestion) => {
          return suggestion.name
            .toLocaleLowerCase()
            .includes(value.toLocaleLowerCase());
        });

      setFilteredSuggestions(suggestionsArray);
    }
  }

  return (
    <Container ref={containerRef}>
      <AutoCompleteDiv ref={selectRef}>
        <InputContainer data-tip data-for="error" ref={wrapperRef}>
          {!optionIsSelected && (
            <ReactTooltip
              id="error"
              place="top"
              type="dark"
              effect="solid"
              offset={{
                left: getContainerWidth() > 0 ? getContainerWidth() : 0,
              }}
              arrowColor={"#c41700"}
            >
              <TooltipText>
                {"You must choose a category to save your new tag"}
              </TooltipText>
            </ReactTooltip>
          )}

          <Input
            onClick={handleClick}
            onBlur={handleLoseFocus}
            onChange={(event) => handleInputChange(event.target.value)}
            onKeyDown={handleInputKeyDown}
            onFocus={handleClick}
            id="input-category"
            value={selectedValue}
            type="text"
            placeholder={placeholder}
            autoComplete="off"
            key={`dropViewWidth ${dropViewWidth} - ${dropviewPosition.left} - ${dropviewPosition.top}`}
            hasError={hasError}
          />
          {openDropView && (
            <StyledAutoCompleteList
              position={dropviewPosition}
              width={dropViewWidth}
            >
              {filteredSuggestions && filteredSuggestions.length > 0 ? (
                <>
                  {filteredSuggestions
                    .filter((c) => {
                      if (c.type === ICategoryType.User) return true;
                      return false;
                    })
                    .map((suggestionItem) => {
                      return (
                        <div
                          ref={(r) =>
                            (suggestionRefMap.current[suggestionItem.name] = r)
                          }
                        >
                          <AutoCompleteSelectItem
                            key={suggestionItem.index + suggestionItem.name}
                            onClick={() => {
                              handleItemClick(suggestionItem);
                            }}
                            name={suggestionItem.name}
                            isActive={suggestionItem.isActive}
                            customStyle={{
                              paddingLeft: "30px",
                            }}
                            alreadyWritten={selectedValue}
                          />
                        </div>
                      );
                    })}

                  {selectedValue &&
                    !suggestions
                      .filter((c) => {
                        if (c.type === ICategoryType.User) return true;
                        return false;
                      })
                      .find(
                        (elem) =>
                          elem.name
                            .toLowerCase()
                            .replace(/\s\s+/g, " ")
                            .trim() ===
                          selectedValue
                            .toLowerCase()
                            .replace(/\s\s+/g, " ")
                            .trim()
                      ) && (
                      <CreateCategory onClick={handleCategoryValueSelect}>
                        <Icon name="tag" width={15} height={15} />

                        <CreateCategoryTitle>
                          <span>No category found for</span>
                          <b> {selectedValue},</b>
                          <span> click here to create.</span>
                        </CreateCategoryTitle>
                      </CreateCategory>
                    )}
                </>
              ) : (
                <NoResultsContainer>
                  <IconContainer>
                    <IconBerserkerMail name="noResultsFound" />
                    <TitleNoResults>No Results Found</TitleNoResults>
                  </IconContainer>
                  <Content
                    css={css`
                      max-width: 90%;
                      ${Ellipsis}
                    `}
                  >{`No categories found for "${selectedValue}"`}</Content>
                  <Content>Do you want to create this as new category?</Content>
                  <CreateTagButton
                    className={"link"}
                    disable={selectedValue === ""}
                    onClick={handleCategoryValueSelect}
                  >
                    Create as new category
                  </CreateTagButton>
                </NoResultsContainer>
              )}
            </StyledAutoCompleteList>
          )}
        </InputContainer>
      </AutoCompleteDiv>
      <SearchIcon>
        <IoMdSearch size={20} />
      </SearchIcon>
      {selectedValue.length > 0 && (
        <StyledClearIcon
          isCreatingTag={false}
          onClick={(e) => {
            e.stopPropagation();
            clearInput();
          }}
        >
          <IoMdClose size={16} />
        </StyledClearIcon>
      )}
    </Container>
  );
}
